'use client';

import React from 'react';
import { motion, easeInOut } from 'motion/react';

interface AnimatedLogoProps {
  shouldStartAnimation?: boolean;
}

const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ shouldStartAnimation = false }) => {
  const isVisible = shouldStartAnimation;

  const pathVariants = {
    hidden: {
      pathLength: 0,
      opacity: 0,
      fillOpacity: 0,
    },
    visible: (i: number) => ({
      pathLength: 1,
      opacity: 1,
      fillOpacity: 0, // stays invisible until stroke finishes
      transition: {
        pathLength: {
          duration: 1.2,
          delay: i * 0.2,
          ease: easeInOut,
        },
        opacity: {
          duration: 0.3,
          delay: i * 0.2,
          ease: easeInOut,
        },
        fillOpacity: {
          duration: 0.5,
          delay: i * 0.2 + 1.2,
          ease: easeInOut,
        },
      },
    }),
  };

  const fillVariants = {
    hidden: {
      opacity: 0,
    },
    visible: (i: number) => ({
      opacity: 1,
      transition: {
        duration: 0.6,
        delay: i * 0.2 + 1.2,
        ease: easeInOut,
      },
    }),
  };

  return (
    <motion.svg 
      width="851" 
      height="243" 
      viewBox="0 0 851 243" 
      className="-mb-[54px] relative z-10" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
    >
      {/* Main text strokes - animated */}
      <motion.path 
        d="M669.178 76C678.06 76 685.624 77.7755 691.907 81.2822C698.171 84.7786 703.071 90.1024 706.597 97.3027L706.6 97.3096C710.117 104.342 711.904 113.268 711.904 124.135V190.301H691.366V126.389C691.366 115.741 689.01 107.505 684.136 101.845C679.244 96.1628 672.174 93.3838 663.094 93.3838C657.251 93.3838 651.961 94.7641 647.248 97.54L646.814 97.8008C641.973 100.612 638.093 104.818 635.163 110.37L635.157 110.38L635.152 110.39C632.361 115.972 630.99 122.746 630.99 130.671V190.301H610.451V78.7051H628.803L630.091 97.1572L630.333 100.63L631.97 97.5586C635.49 90.9592 640.47 85.7599 646.924 81.9463L646.93 81.9424L646.937 81.9375C653.375 77.987 660.779 76.0001 669.178 76Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={0}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M261.607 73C272.162 73.0001 280.934 74.8589 287.966 78.5215C295.005 82.1876 300.262 87.3777 303.769 94.0996L303.775 94.1113L303.781 94.123C307.439 100.706 309.29 108.645 309.29 117.979V190.005H291.907L289.873 169.046L289.561 165.826L288.002 168.66C286.381 171.608 284.464 174.411 282.25 177.067C280.069 179.685 277.447 181.944 274.378 183.844L274.368 183.85L274.36 183.855C271.459 185.741 268.03 187.205 264.06 188.234L264.017 188.246C260.22 189.414 255.964 190.005 251.239 190.005C242.029 190.005 234.458 188.444 228.479 185.382C222.654 182.398 218.317 178.475 215.419 173.626L215.143 173.154C212.224 167.896 210.767 162.281 210.767 156.295C210.767 148.964 212.67 142.795 216.43 137.733L216.438 137.723C220.216 132.492 225.61 128.467 232.674 125.671C239.74 122.874 248.249 121.459 258.226 121.459H288.751V120.459C288.751 113.757 287.762 108.121 285.734 103.596C283.697 99.0536 280.621 95.6489 276.505 93.4326C272.556 91.0754 267.568 89.9327 261.607 89.9326C254.684 89.9326 248.699 91.6252 243.702 95.0547C238.852 98.2379 235.767 102.947 234.421 109.091H213.83C214.831 101.778 217.423 95.5702 221.587 90.4346C226.134 84.8614 231.933 80.6016 239.001 77.6562L239.009 77.6533L239.018 77.6494C246.1 74.5511 253.626 73 261.607 73ZM260.48 135.461C253.955 135.461 248.56 136.293 244.343 138.007C240.299 139.562 237.252 141.776 235.305 144.696C233.38 147.583 232.433 150.937 232.433 154.718C232.433 158.624 233.294 162.042 235.059 164.931L235.075 164.959L235.095 164.986C237.016 167.708 239.721 169.841 243.161 171.405C246.628 172.981 250.759 173.749 255.521 173.749C260.468 173.749 264.981 172.742 269.04 170.713C273.069 168.699 276.486 165.983 279.281 162.567C282.211 159.021 284.375 155.084 285.767 150.762C287.301 146.312 288.146 141.78 288.3 137.171L288.301 137.154V135.461H260.48Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={1}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M382.018 76C390.9 76 398.463 77.7755 404.746 81.2822C411.011 84.7786 415.91 90.1024 419.437 97.3027L419.44 97.3096C422.956 104.342 424.743 113.268 424.743 124.135V190.301H404.205V126.389C404.205 115.741 401.85 107.505 396.976 101.845C392.083 96.1628 385.013 93.3838 375.933 93.3838C370.091 93.3838 364.8 94.7641 360.087 97.54L359.654 97.8008C354.812 100.612 350.933 104.818 348.002 110.37L347.996 110.38L347.991 110.39C345.2 115.972 343.829 122.746 343.829 130.671V190.301H323.29V78.7051H341.643L342.93 97.1572L343.172 100.63L344.81 97.5586C348.329 90.9592 353.309 85.7599 359.763 81.9463L359.77 81.9424L359.777 81.9375C366.214 77.987 373.618 76.0001 382.018 76Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={2}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M553.495 27V189.984H535.382L532.949 170.575L532.609 167.868L531.119 170.153C528.924 173.519 526.059 176.755 522.514 179.857C519.007 182.777 514.753 185.202 509.736 187.12C504.762 189.022 499.027 189.984 492.513 189.984C481.859 189.984 472.503 187.471 464.415 182.471C456.312 177.312 449.976 170.39 445.4 161.688C440.97 152.828 438.744 142.767 438.744 131.482C438.744 120.044 440.972 109.986 445.397 101.282L445.396 101.281C449.973 92.5751 456.311 85.7282 464.415 80.7188L464.419 80.7158C472.66 75.565 482.09 72.9795 492.739 72.9795C501.466 72.9795 509.113 74.7529 515.709 78.2705L515.716 78.2754L515.724 78.2793C522.318 81.6493 527.433 86.4744 531.091 92.7666L532.957 95.9736V27H553.495ZM496.12 90.5879C489.395 90.5879 483.296 92.2557 477.849 95.6006L477.341 95.9189C471.765 99.3267 467.435 104.127 464.351 110.29C461.26 116.318 459.733 123.393 459.733 131.482C459.733 139.573 461.26 146.718 464.346 152.891C467.43 159.058 471.762 163.861 477.341 167.271C482.918 170.679 489.187 172.376 496.12 172.376C503.344 172.376 509.765 170.683 515.348 167.271C520.926 163.862 525.184 159.058 528.118 152.891C531.204 146.718 532.731 139.573 532.731 131.482C532.731 123.646 531.298 116.761 528.398 110.858L528.113 110.29C525.179 104.127 520.924 99.3261 515.348 95.9189H515.347C509.765 92.3576 503.345 90.5879 496.12 90.5879Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={3}
        variants={pathVariants}
      />
      
      {/* Accent strokes - animated with different timing */}
      <motion.path 
        d="M549 38V185H537V38H549Z" 
        stroke="#272D57" 
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#272D57"
        custom={4}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M196.766 95.8916H189.653C184.908 95.8916 180.382 96.5032 176.08 97.7324C171.745 98.821 167.865 100.762 164.452 103.555L164.443 103.562L164.434 103.569C161.131 106.401 158.565 110.231 156.715 115.012C154.854 119.82 153.943 125.736 153.943 132.728V188.301H133.404V76.7051H151.779L153.723 97.2109L154.052 100.686L155.612 97.5635C158.111 92.5662 161.336 88.393 165.283 85.0303L165.299 85.0166C169.255 81.4998 173.877 78.7839 179.174 76.8711L179.173 76.8701C184.344 75.0542 190.205 74.097 196.766 74.0088V95.8916Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={5}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M150 144V185H138V144H150Z" 
        stroke="#D53333" 
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#D53333"
        custom={6}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M24.8655 26.7201V95.6654L26.7112 92.7435C30.2251 87.18 35.1397 82.4769 41.4836 78.641L41.4895 78.6371C47.9096 74.6975 55.8321 72.6996 65.3079 72.6996C75.9569 72.6996 85.3085 75.2855 93.3958 80.432L93.4065 80.4388C101.507 85.4463 107.769 92.366 112.197 101.222L112.2 101.231L112.205 101.24C116.777 109.941 119.077 119.994 119.077 131.427C119.077 142.555 116.779 152.537 112.202 161.396L112.197 161.408C107.769 170.264 101.507 177.183 93.4065 182.191C85.3178 187.191 75.9624 189.704 65.3079 189.704C59.2454 189.704 53.7248 188.818 48.7366 187.057L48.7288 187.054L48.72 187.051C43.8638 185.432 39.6129 183.157 35.9563 180.232C32.2801 177.291 29.197 173.913 26.7024 170.098L25.2502 167.877L24.8752 170.503L22.1331 189.704H4.32642V26.7201H24.8655ZM61.7024 90.308C54.6954 90.308 48.4449 91.9712 42.9827 95.3197L42.4563 95.6498C37.2022 98.954 33.0499 103.563 29.9963 109.446L29.7083 110.01C26.6171 116.038 25.0911 123.113 25.0911 131.203C25.0911 139.293 26.6181 146.438 29.7043 152.611C32.7844 158.771 37.0357 163.573 42.4622 166.984L42.4729 166.991C48.0567 170.403 54.4778 172.096 61.7024 172.096C68.7758 172.096 75.0463 170.403 80.4797 166.991C86.0534 163.584 90.3829 158.787 93.4661 152.628C96.7088 146.452 98.3137 139.3 98.3137 131.203C98.3137 123.359 96.8079 116.468 93.761 110.561L93.4612 109.993C90.3815 103.844 86.0599 99.0536 80.4973 95.6498C75.0617 92.0808 68.7849 90.308 61.7024 90.308Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={7}
        variants={pathVariants}
      />
      
      {/* Special elements with drop shadows */}
      <motion.g 
        filter="url(#filter0_d_10_64)"
        custom={8}
        variants={fillVariants}
      >
        <path d="M21 6V77H9V6H21Z" stroke="#272D57" strokeWidth="12" shapeRendering="crispEdges" />
      </motion.g>
      
      <motion.path 
        d="M592.62 78.8105V190.407H572.081V78.8105H592.62ZM582.576 29C586.547 29.0001 589.826 30.2676 592.481 32.7832L592.492 32.793L592.501 32.8018C595.131 35.1541 596.451 38.1824 596.451 41.9736C596.451 45.9277 595.122 49.1146 592.481 51.6162C589.826 54.1318 586.547 55.3993 582.576 55.3994C578.573 55.3994 575.303 54.2035 572.695 51.8477L572.445 51.6162C569.96 49.1215 568.7 45.9348 568.7 41.9736C568.7 38.1645 569.958 35.1313 572.443 32.7842L572.445 32.7832C575.093 30.2742 578.444 29 582.576 29Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={9}
        variants={pathVariants}
      />
      
      <motion.path 
        d="M588.176 101.407V185.407H576.176V101.407H588.176Z" 
        stroke="#272D57" 
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#272D57"
        custom={10}
        variants={pathVariants}
      />
      
      {/* Circle with mask - animated fill */}
      <motion.mask 
        id="path-12-inside-1_10_64" 
        fill="white"
        custom={11}
        variants={fillVariants}
      >
        <ellipse cx="582.5" cy="42.5" rx="15.5" ry="14.5" />
      </motion.mask>
      
      <motion.path 
        d="M598 42.5H583C583 42.3092 583.046 42.1145 583.114 41.9643C583.176 41.8282 583.229 41.7843 583.213 41.799C583.175 41.8341 582.959 42 582.5 42V57V72C598.387 72 613 59.719 613 42.5H598ZM582.5 57V42C582.041 42 581.825 41.8341 581.787 41.799C581.771 41.7843 581.824 41.8282 581.886 41.9643C581.954 42.1145 582 42.3092 582 42.5H567H552C552 59.719 566.613 72 582.5 72V57ZM567 42.5H582C582 42.6908 581.954 42.8855 581.886 43.0357C581.824 43.1718 581.771 43.2157 581.787 43.201C581.825 43.1659 582.041 43 582.5 43V28V13C566.613 13 552 25.281 552 42.5H567ZM582.5 28V43C582.959 43 583.175 43.1659 583.213 43.201C583.229 43.2157 583.176 43.1718 583.114 43.0357C583.046 42.8855 583 42.6908 583 42.5H598H613C613 25.281 598.387 13 582.5 13V28Z" 
        fill="#D53333" 
        mask="url(#path-12-inside-1_10_64)"
        custom={11}
        variants={fillVariants}
      />
      
      <motion.path 
        d="M777.42 70.6995C784.003 70.6995 789.936 71.596 795.207 73.4045H838.147V90.9026L837.202 90.9543L818.032 92.0012C821.531 98.0514 823.272 104.891 823.272 112.494C823.272 120.169 821.429 127.176 817.734 133.493L817.735 133.494C814.179 139.833 808.926 144.845 802.015 148.532C795.223 152.237 787.011 154.065 777.42 154.065C771.559 154.065 766.198 153.402 761.345 152.069L752.216 160.528C752.23 160.538 752.245 160.548 752.26 160.558C753.661 161.395 755.594 162.189 758.093 162.924C760.579 163.655 763.98 164.323 768.315 164.921C772.648 165.519 778.337 166.118 785.389 166.718C795.663 167.474 803.979 169.292 810.287 172.213C816.74 175.132 821.502 179.086 824.484 184.109C827.446 189.097 828.907 194.99 828.907 201.748C828.907 208.407 827.046 214.748 823.35 220.755C819.776 226.807 814.132 231.727 806.498 235.545L806.499 235.546C798.949 239.398 789.236 241.29 777.42 241.29C766.974 241.29 757.702 239.928 749.617 237.185V237.186C741.506 234.584 735.165 230.505 730.662 224.915C726.144 219.306 723.904 212.465 723.904 204.453C723.904 200.245 724.84 195.979 726.689 191.663L726.695 191.651C728.565 187.443 731.587 183.429 735.72 179.603C736.553 178.832 737.444 178.079 738.391 177.341C736.21 176.351 734.24 175.304 732.484 174.196L732.474 174.189L732.464 174.182C729.714 172.348 727.331 170.429 725.324 168.422L725.031 168.129V162.125L725.313 161.834L743.994 142.592C741.105 139.948 738.66 136.921 736.663 133.51L736.658 133.502L736.654 133.494C733.109 127.175 731.342 120.167 731.342 112.494C731.342 104.675 733.107 97.6631 736.659 91.4856L736.663 91.4797C740.254 85.3456 745.364 80.4525 751.964 76.801L752.607 76.4514C759.544 72.6015 767.828 70.6995 777.42 70.6995ZM760.577 183.5C755.892 185.781 752.648 188.306 750.754 191.034C748.282 194.967 747.091 198.684 747.091 202.199C747.091 206.491 748.372 210 750.892 212.8C753.456 215.649 756.974 217.819 761.499 219.279H761.498C766.191 220.744 771.495 221.485 777.42 221.485C783.189 221.485 788.172 220.671 792.392 219.071C796.617 217.468 799.837 215.232 802.108 212.394C804.363 209.576 805.495 206.268 805.495 202.425C805.495 197.906 803.816 194.117 800.417 190.998C798.771 189.561 796.304 188.359 792.935 187.444C789.567 186.528 785.358 185.915 780.291 185.617L780.28 185.616C772.737 185.087 766.167 184.382 760.577 183.5ZM777.42 90.9563C770.239 90.9563 764.556 92.8571 760.27 96.5715L760.262 96.5784L760.254 96.5842C756.031 100.104 753.853 105.347 753.853 112.494C753.853 119.641 756.031 124.97 760.27 128.643H760.269C764.553 132.207 770.238 134.033 777.42 134.033C784.609 134.033 790.213 132.203 794.343 128.646C798.43 124.976 800.536 119.646 800.536 112.494C800.536 105.336 798.426 100.089 794.348 96.5735L794.34 96.5667L794.333 96.5608C790.203 92.8578 784.602 90.9563 777.42 90.9563Z" 
        stroke="#DEDEDE" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="#DEDEDE"
        custom={12}
        variants={pathVariants}
      />
      
      {/* Final accent element */}
      <motion.g 
        filter="url(#filter1_d_10_64)"
        custom={13}
        variants={fillVariants}
      >
        <path d="M819.762 71.7059L840 71V93.5882L815 95L819.762 71.7059Z" fill="#D53333" />
      </motion.g>
      
      {/* Filter definitions */}
      <defs>
        <filter id="filter0_d_10_64" x="0" y="0" width="38" height="99" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="4" dy="9" />
          <feGaussianBlur stdDeviation="3.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_10_64" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_10_64" result="shape" />
        </filter>
        <filter id="filter1_d_10_64" x="812" y="71" width="39" height="40" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="4" dy="9" />
          <feGaussianBlur stdDeviation="3.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_10_64" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_10_64" result="shape" />
        </filter>
      </defs>
    </motion.svg>
  );
};

export default AnimatedLogo;
